import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionService } from '../services/subscriptionService';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [hasPremium, setHasPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load subscription data when user changes
  useEffect(() => {
    if (user) {
      loadSubscriptionData();
    } else {
      // Reset state when user logs out
      setSubscription(null);
      setHasPremium(false);
      setLoading(false);
      setError(null);
    }
  }, [user]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [subscriptionData, premiumAccess] = await Promise.all([
        subscriptionService?.getCurrentSubscription(),
        subscriptionService?.hasPremiumAccess()
      ]);

      setSubscription(subscriptionData);
      setHasPremium(premiumAccess);
    } catch (err) {
      console.error('Error loading subscription data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const checkFeatureAccess = async (featureName) => {
    try {
      return await subscriptionService?.hasFeatureAccess(featureName);
    } catch (error) {
      console.error('Error checking feature access:', error);
      return false;
    }
  };

  const refreshSubscription = () => {
    if (user) {
      loadSubscriptionData();
    }
  };

  // Calculate subscription status
  const getSubscriptionStatus = () => {
    if (!subscription) return { status: 'free', message: 'No subscription' };
    
    if (subscription?.status === 'trial') {
      const daysLeft = Math.ceil((new Date(subscription.trialEndDate) - new Date()) / (1000 * 60 * 60 * 24));
      return {
        status: 'trial',
        message: `Trial: ${daysLeft} days remaining`,
        daysLeft,
        isExpiringSoon: daysLeft <= 3
      };
    }
    
    if (subscription?.status === 'active') {
      return {
        status: 'active',
        message: `${subscription?.tier === 'business' ? 'Commercial Operation' : 'Home Grower'} Plan`,
        tier: subscription?.tier
      };
    }
    
    return { 
      status: subscription?.status, 
      message: `Subscription ${subscription?.status}` 
    };
  };

  const value = {
    // State
    subscription,
    hasPremium,
    loading,
    error,
    
    // Methods
    checkFeatureAccess,
    refreshSubscription,
    getSubscriptionStatus,
    
    // Computed values
    isTrialUser: subscription?.status === 'trial',
    isPremiumUser: hasPremium || subscription?.tier === 'business',
    isFreeTrial: subscription?.status === 'trial' && subscription?.isTrialActive,
    canAccessCalculators: hasPremium || subscription?.isTrialActive,
    needsUpgrade: !hasPremium && !(subscription?.status === 'trial' && subscription?.isTrialActive)
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};